import { useEffect, useState } from "react";
import api, { errMsg, formatTL, formatNumber, formatDate } from "@/lib/api";
import PageHeader from "@/components/app/PageHeader";
import ExportButtons from "@/components/app/ExportButtons";
import { ChevronDown, ChevronUp, FileText, Users, Factory, BarChart3, TrendingUp, TrendingDown, Minus, Wallet, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const monthLabel = (m) => {
  if (!m || m === "unknown") return "Belirsiz";
  const [y, mo] = m.split("-");
  const names = ["Ocak","Şubat","Mart","Nisan","Mayıs","Haziran","Temmuz","Ağustos","Eylül","Ekim","Kasım","Aralık"];
  return `${names[parseInt(mo, 10) - 1] || mo} ${y}`;
};

export default function InvoicesMonthlyPage() {
  const [tab, setTab] = useState("sales"); // "sales" or "supplier"
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(new Set());
  const [view, setView] = useState("month"); // "month" | "counterparty" | "payment"
  const [plan, setPlan] = useState(null);
  const [planExpanded, setPlanExpanded] = useState(new Set());
  const [marking, setMarking] = useState(null);

  const load = async (t) => {
    try {
      const { data } = await api.get(`/reports/invoices-monthly?type=${t}`);
      setData(data);
      setExpanded(new Set());
    } catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { load(tab); }, [tab]);

  const loadPlan = async () => {
    try {
      const { data: p } = await api.get("/reports/supplier-payment-plan");
      setPlan(p);
      // Vadesi geçmiş ve bu ay otomatik açık gelsin
      setPlanExpanded(new Set(p.months.filter((m) => m.overdue || m.is_current).map((m) => m.month)));
    } catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { if (tab === "supplier" && view === "payment" && !plan) loadPlan(); }, [tab, view]); // eslint-disable-line

  const switchTab = (t) => {
    setTab(t);
    if (t === "sales" && view === "payment") setView("month");
  };

  const markPaid = async (inv) => {
    if (!window.confirm(`${inv.supplier_or_type} — ${formatTL(inv.amount)} tutarındaki fatura ÖDENDİ olarak işaretlensin mi?`)) return;
    setMarking(inv.id);
    try {
      await api.put(`/invoices/${inv.id}`, {
        invoice_no: inv.invoice_no, supplier_id: inv.supplier_id, supplier_or_type: inv.supplier_or_type,
        date: inv.date, due_date: inv.due_date, amount: inv.amount, status: "paid",
        category: inv.category, month: inv.month, notes: inv.notes,
      });
      toast.success("Ödendi olarak işaretlendi");
      await loadPlan();
      load(tab);
    } catch (err) { toast.error(errMsg(err)); }
    finally { setMarking(null); }
  };

  const toggle = (k) => {
    const s = new Set(expanded);
    if (s.has(k)) s.delete(k); else s.add(k);
    setExpanded(s);
  };

  if (!data) return <div className="p-8 text-sm text-neutral-500">Yükleniyor...</div>;

  const isSales = tab === "sales";
  const title = isSales ? "Kesilen Faturalar (Aylık)" : "Tedarikçi Faturaları (Aylık)";
  const cpLabel = isSales ? "Firma" : "Tedarikçi";
  const chartData = data.months.slice().reverse().map(m => ({ ...m, label: monthLabel(m.month) }));

  return (
    <>
      <PageHeader
        title="Aylık Fatura Raporu"
        subtitle="Kesilen ve alınan faturaların firma/tedarikçi bazlı aylık kırılımı."
        testid="invoices-monthly-header"
        actions={
          <ExportButtons
            filename={`aylik_${tab}_faturalar`}
            title={title}
            subtitle={`${data.grand.months} ay · ${data.grand.counterparties} ${cpLabel.toLowerCase()} · Toplam: ${formatTL(data.grand.total)}`}
            sections={[
              { name: "Aylık", heading: `${title} — Aylık`,
                columns: [
                  { key: "month", label: "Ay" },
                  { key: "count", label: "Fatura Sayısı", format: "num" },
                  { key: "total", label: "Toplam", format: "money" },
                  { key: "paid", label: "Ödenmiş", format: "money" },
                  { key: "unpaid", label: "Ödenmemiş", format: "money" },
                ],
                rows: data.months,
              },
              { name: cpLabel, heading: `${cpLabel} Bazlı Toplam`,
                columns: [
                  { key: "name", label: cpLabel },
                  { key: "count", label: "Fatura Sayısı", format: "num" },
                  { key: "total", label: "Toplam", format: "money" },
                ],
                rows: data.counterparties,
              },
            ]}
          />
        }
      />

      {/* Tab switcher */}
      <div className="flex items-center gap-2 mb-4">
        <button data-testid="tab-sales" onClick={() => switchTab("sales")} className={`flex items-center gap-2 px-4 py-2.5 text-xs uppercase tracking-widest font-semibold border-2 transition-colors ${tab === "sales" ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-[#E5E5E5] hover:bg-[#F5F5F5]"}`}>
          <Users className="w-3.5 h-3.5" /> Kesilen (Firmalar)
        </button>
        <button data-testid="tab-supplier" onClick={() => switchTab("supplier")} className={`flex items-center gap-2 px-4 py-2.5 text-xs uppercase tracking-widest font-semibold border-2 transition-colors ${tab === "supplier" ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-[#E5E5E5] hover:bg-[#F5F5F5]"}`}>
          <Factory className="w-3.5 h-3.5" /> Tedarikçi Faturaları
        </button>
        <div className="flex-1"></div>
        <div className="flex items-center gap-1 border border-[#E5E5E5] rounded-md p-0.5">
          <button onClick={() => setView("month")} className={`px-3 py-1.5 text-[10px] uppercase tracking-widest font-semibold ${view === "month" ? "bg-[#0A0A0A] text-white" : "text-neutral-600 hover:bg-[#F5F5F5]"}`}>
            Aylık Görünüm
          </button>
          <button onClick={() => setView("counterparty")} className={`px-3 py-1.5 text-[10px] uppercase tracking-widest font-semibold ${view === "counterparty" ? "bg-[#0A0A0A] text-white" : "text-neutral-600 hover:bg-[#F5F5F5]"}`}>
            {cpLabel} Bazlı
          </button>
          {tab === "supplier" && (
            <button data-testid="view-payment-plan" onClick={() => setView("payment")} className={`flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-widest font-semibold ${view === "payment" ? "bg-[#DC2626] text-white" : "text-[#DC2626] hover:bg-[#FEF2F2]"}`}>
              <Wallet className="w-3.5 h-3.5" /> Ödeme Planı
            </button>
          )}
        </div>
      </div>

      {/* Comparison Panel */}
      {view !== "payment" && data.comparison && (
        <ComparisonPanel c={data.comparison} cpLabel={cpLabel} />
      )}

      {/* KPIs */}
      {view !== "payment" && (
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <div className="bg-white border border-[#E5E5E5] p-4">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">Toplam Fatura</div>
          <div className="num font-bold text-xl mt-1">{formatTL(data.grand.total)}</div>
        </div>
        <div className="bg-white border border-[#E5E5E5] p-4">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">Fatura Sayısı</div>
          <div className="num font-bold text-xl mt-1">{formatNumber(data.grand.count)}</div>
        </div>
        <div className="bg-white border border-[#E5E5E5] p-4">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">Ödenmiş</div>
          <div className="num font-bold text-xl mt-1 text-[#16A34A]">{formatTL(data.grand.paid)}</div>
        </div>
        <div className="bg-white border border-[#E5E5E5] p-4">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">Ödenmemiş</div>
          <div className="num font-bold text-xl mt-1 text-[#DC2626]">{formatTL(data.grand.unpaid)}</div>
        </div>
        <div className="bg-white border border-[#E5E5E5] p-4">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">{cpLabel} Sayısı</div>
          <div className="num font-bold text-xl mt-1 text-[#0020C2]">{formatNumber(data.grand.counterparties)}</div>
        </div>
      </div>
      )}

      {/* Chart */}
      {view !== "payment" && chartData.length > 0 && (
        <div className="bg-white border border-[#E5E5E5] p-5 mb-6 rounded-md">
          <div className="text-[11px] uppercase tracking-widest font-semibold text-neutral-500 mb-3 flex items-center gap-2">
            <BarChart3 className="w-3.5 h-3.5" /> Aylık Trend
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
              <XAxis dataKey="label" style={{ fontSize: 10 }} />
              <YAxis tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} style={{ fontSize: 10 }} />
              <Tooltip formatter={(v) => formatTL(v)} />
              <Bar dataKey="paid" name="Ödenmiş" stackId="a" fill="#16A34A" />
              <Bar dataKey="unpaid" name="Ödenmemiş" stackId="a" fill="#DC2626" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Monthly view */}
      {view === "month" && (
        <div className="space-y-3">
          {data.months.length === 0 ? (
            <div className="bg-white border border-[#E5E5E5] p-16 text-center text-sm text-neutral-500">
              <FileText className="w-8 h-8 mx-auto mb-3 text-neutral-400" />
              Henüz fatura kaydı yok.
            </div>
          ) : data.months.map(m => {
            const isOpen = expanded.has(m.month);
            return (
              <div key={m.month} className="bg-white border border-[#E5E5E5] rounded-md" data-testid={`inv-month-${m.month}`}>
                <button onClick={() => toggle(m.month)} className="w-full px-5 py-4 flex items-center justify-between text-left">
                  <div className="flex items-center gap-3">
                    {isOpen ? <ChevronUp className="w-4 h-4 text-neutral-500" /> : <ChevronDown className="w-4 h-4 text-neutral-500" />}
                    <div>
                      <div className="font-heading font-bold text-lg">{monthLabel(m.month)}</div>
                      <div className="text-xs text-neutral-500 mt-0.5"><span className="num font-semibold">{m.count}</span> fatura · {m.by_counterparty.length} {cpLabel.toLowerCase()}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-5 flex-wrap">
                    {m.paid > 0 && (
                      <div className="text-right">
                        <div className="text-[10px] uppercase tracking-widest text-neutral-500">Ödenmiş</div>
                        <div className="num font-bold text-sm text-[#16A34A]">{formatTL(m.paid)}</div>
                      </div>
                    )}
                    {m.unpaid > 0 && (
                      <div className="text-right">
                        <div className="text-[10px] uppercase tracking-widest text-neutral-500">Ödenmemiş</div>
                        <div className="num font-bold text-sm text-[#DC2626]">{formatTL(m.unpaid)}</div>
                      </div>
                    )}
                    <div className="text-right">
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500">Toplam</div>
                      <div className="num font-bold text-base">{formatTL(m.total)}</div>
                    </div>
                  </div>
                </button>
                {isOpen && (
                  <div className="border-t border-[#F5F5F5] p-4">
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-[#FAFAFA]">
                          <tr>
                            {[cpLabel, "Fatura Sayısı", "Toplam"].map((h, i) => (
                              <th key={h} className={`px-3 py-2 text-[10px] uppercase tracking-widest text-neutral-500 font-semibold ${i > 0 ? "text-right" : "text-left"}`}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {m.by_counterparty.map(cp => (
                            <tr key={cp.id || cp.name} className="border-t border-[#F5F5F5]">
                              <td className="px-3 py-2 font-medium">{cp.name}</td>
                              <td className="px-3 py-2 text-right num">{formatNumber(cp.count)}</td>
                              <td className="px-3 py-2 text-right num font-semibold">{formatTL(cp.total)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Counterparty view */}
      {view === "counterparty" && (
        <div className="bg-white border border-[#E5E5E5] rounded-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
                <tr>
                  {[cpLabel, "Fatura Sayısı", "Aylık Ortalama", "Toplam", "Aylık Dağılım"].map((h, i) => (
                    <th key={h} className={`px-3 py-3 text-[11px] uppercase tracking-widest text-neutral-500 font-semibold ${i >= 1 && i <= 3 ? "text-right" : "text-left"}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.counterparties.length === 0 ? (
                  <tr><td colSpan={5} className="p-10 text-center text-sm text-neutral-500">Kayıt yok</td></tr>
                ) : data.counterparties.map(c => (
                  <tr key={c.id || c.name} className="border-b border-[#F5F5F5]">
                    <td className="px-3 py-3 font-semibold">{c.name}</td>
                    <td className="px-3 py-3 text-right num">{formatNumber(c.count)}</td>
                    <td className="px-3 py-3 text-right num text-neutral-600">{formatTL(c.total / (c.monthly.length || 1))}</td>
                    <td className="px-3 py-3 text-right num font-bold text-[#0A0A0A]">{formatTL(c.total)}</td>
                    <td className="px-3 py-3">
                      <div className="flex gap-1 items-end h-8">
                        {c.monthly.slice(-12).map(mm => {
                          const maxAmt = Math.max(...c.monthly.map(x => x.amount));
                          const h = maxAmt > 0 ? Math.max(4, (mm.amount / maxAmt) * 32) : 4;
                          return (
                            <div key={mm.month} title={`${monthLabel(mm.month)}: ${formatTL(mm.amount)}`} className="w-2 bg-[#0020C2] rounded-t" style={{ height: `${h}px` }} />
                          );
                        })}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Ödeme Planı (Vade bazlı yapılacak ödemeler) */}
      {view === "payment" && tab === "supplier" && (
        !plan ? (
          <div className="p-8 text-sm text-neutral-500">Ödeme planı yükleniyor...</div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <div className="bg-white border-l-4 border-l-[#DC2626] border border-[#E5E5E5] p-4" data-testid="plan-kpi-overdue">
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 flex items-center gap-1"><AlertTriangle className="w-3 h-3 text-[#DC2626]" /> Vadesi Geçmiş</div>
                <div className="num font-bold text-xl mt-1 text-[#DC2626]">{formatTL(plan.grand.overdue_total)}</div>
              </div>
              <div className="bg-white border-l-4 border-l-[#0020C2] border border-[#E5E5E5] p-4" data-testid="plan-kpi-current">
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">Bu Ay Ödenecek</div>
                <div className="num font-bold text-xl mt-1 text-[#0020C2]">{formatTL(plan.grand.current_month_due)}</div>
              </div>
              <div className="bg-white border-l-4 border-l-[#F59E0B] border border-[#E5E5E5] p-4" data-testid="plan-kpi-next">
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">Gelecek Ay</div>
                <div className="num font-bold text-xl mt-1 text-[#F59E0B]">{formatTL(plan.grand.next_month_due)}</div>
              </div>
              <div className="bg-white border-l-4 border-l-[#0A0A0A] border border-[#E5E5E5] p-4" data-testid="plan-kpi-total">
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">Toplam Ödenecek</div>
                <div className="num font-bold text-xl mt-1">{formatTL(plan.grand.total_due)}</div>
                <div className="text-[10px] text-neutral-500 num">{plan.grand.count} fatura</div>
              </div>
            </div>

            <div className="space-y-3">
              {plan.months.length === 0 ? (
                <div className="bg-white border border-[#E5E5E5] p-16 text-center text-sm text-neutral-500">
                  <CheckCircle2 className="w-8 h-8 mx-auto mb-3 text-[#16A34A]" />
                  Ödenmemiş tedarikçi faturası yok. Tüm ödemeler tamamlanmış görünüyor.
                </div>
              ) : plan.months.map((m) => {
                const isOpen = planExpanded.has(m.month);
                const togglePlan = () => {
                  const s = new Set(planExpanded);
                  if (s.has(m.month)) s.delete(m.month); else s.add(m.month);
                  setPlanExpanded(s);
                };
                return (
                  <div key={m.month} className={`bg-white border rounded-md ${m.overdue ? "border-[#FECACA]" : m.is_current ? "border-[#0020C2]" : "border-[#E5E5E5]"}`} data-testid={`plan-month-${m.month}`}>
                    <button onClick={togglePlan} className="w-full px-5 py-4 flex items-center justify-between text-left">
                      <div className="flex items-center gap-3">
                        {isOpen ? <ChevronUp className="w-4 h-4 text-neutral-500" /> : <ChevronDown className="w-4 h-4 text-neutral-500" />}
                        <div>
                          <div className="font-heading font-bold text-lg flex items-center gap-2">
                            {monthLabel(m.month)}
                            {m.overdue && <span className="px-2 py-0.5 text-[9px] uppercase tracking-widest font-bold bg-[#DC2626] text-white">VADESİ GEÇMİŞ</span>}
                            {m.is_current && <span className="px-2 py-0.5 text-[9px] uppercase tracking-widest font-bold bg-[#0020C2] text-white">BU AY</span>}
                          </div>
                          <div className="text-xs text-neutral-500 mt-0.5"><span className="num font-semibold">{m.count}</span> ödenecek fatura</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-[10px] uppercase tracking-widest text-neutral-500">Ödenecek</div>
                        <div className={`num font-bold text-base ${m.overdue ? "text-[#DC2626]" : ""}`}>{formatTL(m.total)}</div>
                      </div>
                    </button>
                    {isOpen && (
                      <div className="border-t border-[#F5F5F5] p-4 overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-[#FAFAFA]">
                            <tr>
                              {["Tedarikçi", "Fatura No", "Fatura Tarihi", "Vade", "Durum", "Tutar", ""].map((h, i) => (
                                <th key={h} className={`px-3 py-2 text-[10px] uppercase tracking-widest text-neutral-500 font-semibold ${i >= 5 ? "text-right" : "text-left"}`}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {m.invoices.map((inv) => (
                              <tr key={inv.id} className="border-t border-[#F5F5F5]" data-testid={`plan-invoice-${inv.id}`}>
                                <td className="px-3 py-2 font-medium">{inv.supplier_or_type}</td>
                                <td className="px-3 py-2 num text-neutral-600">{inv.invoice_no}</td>
                                <td className="px-3 py-2 num">{formatDate(inv.date)}</td>
                                <td className="px-3 py-2 num">
                                  {inv.effective_due ? (
                                    <span>
                                      {formatDate(inv.effective_due)}
                                      {inv.due_source === "terms" && <span className="ml-1 text-[8px] uppercase tracking-widest font-bold text-[#0020C2]" title="Tedarikçi ödeme koşulundan otomatik hesaplandı">OTO</span>}
                                    </span>
                                  ) : <span className="text-neutral-400">—</span>}
                                </td>
                                <td className="px-3 py-2">
                                  <span className={`text-[9px] uppercase tracking-widest font-semibold px-1.5 py-0.5 text-white ${inv.status === "partial" ? "bg-[#F59E0B]" : "bg-[#DC2626]"}`}>
                                    {inv.status === "partial" ? "Kısmi" : "Ödenmedi"}
                                  </span>
                                </td>
                                <td className="px-3 py-2 text-right num font-semibold">{formatTL(inv.amount)}</td>
                                <td className="px-3 py-2 text-right">
                                  <button
                                    data-testid={`plan-mark-paid-${inv.id}`}
                                    disabled={marking === inv.id}
                                    onClick={() => markPaid(inv)}
                                    className="inline-flex items-center gap-1 px-2 py-1 text-[9px] uppercase tracking-widest font-bold border border-[#16A34A] text-[#16A34A] hover:bg-[#16A34A] hover:text-white disabled:opacity-50 transition-colors"
                                  >
                                    <CheckCircle2 className="w-3 h-3" /> {marking === inv.id ? "..." : "Ödendi"}
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )
      )}
    </>
  );
}

function GrowthBadge({ pct }) {
  if (pct === null || pct === undefined) return <span className="text-neutral-400 text-xs">—</span>;
  const isUp = pct > 0;
  const isFlat = pct === 0;
  const Icon = isFlat ? Minus : isUp ? TrendingUp : TrendingDown;
  const cls = isFlat ? "text-neutral-500 bg-neutral-100" : isUp ? "text-[#16A34A] bg-[#DCFCE7]" : "text-[#DC2626] bg-[#FEE2E2]";
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${cls}`}>
      <Icon className="w-3 h-3" strokeWidth={2.5} />
      {isUp && "+"}{pct.toFixed(1)}%
    </span>
  );
}

function ComparisonPanel({ c, cpLabel }) {
  const monthLabel = (m) => {
    if (!m || m === "unknown") return "-";
    const [y, mo] = m.split("-");
    const names = ["Ocak","Şubat","Mart","Nisan","Mayıs","Haziran","Temmuz","Ağustos","Eylül","Ekim","Kasım","Aralık"];
    return `${names[parseInt(mo, 10) - 1] || mo} ${y}`;
  };
  return (
    <div className="bg-white border border-[#E5E5E5] p-5 mb-6 rounded-md" data-testid="comparison-panel">
      <div className="text-[11px] uppercase tracking-widest font-semibold text-neutral-500 mb-4 flex items-center gap-2">
        <BarChart3 className="w-3.5 h-3.5" /> Karşılaştırma
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
        {/* MoM */}
        <div className="border-l-4 border-l-[#0020C2] bg-[#FAFAFA] p-4">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Aylık Değişim (MoM)</div>
          <div className="mt-2 flex items-baseline gap-3">
            <div className="num font-bold text-xl">{formatTL(c.current_total)}</div>
            <GrowthBadge pct={c.mom_growth_pct} />
          </div>
          <div className="text-[10px] text-neutral-500 mt-1 num">
            {monthLabel(c.current_month)} vs {monthLabel(c.previous_month)}: <span className="text-neutral-600">{formatTL(c.previous_total)}</span>
          </div>
        </div>
        {/* YoY */}
        <div className="border-l-4 border-l-[#F59E0B] bg-[#FAFAFA] p-4">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Yıllık Değişim (YoY)</div>
          <div className="mt-2 flex items-baseline gap-3">
            <div className="num font-bold text-xl">{formatTL(c.current_total)}</div>
            <GrowthBadge pct={c.yoy_growth_pct} />
          </div>
          <div className="text-[10px] text-neutral-500 mt-1 num">
            {monthLabel(c.current_month)} vs {monthLabel(c.yoy_month)}: <span className="text-neutral-600">{formatTL(c.yoy_total)}</span>
          </div>
        </div>
        {/* YTD */}
        <div className="border-l-4 border-l-[#16A34A] bg-[#FAFAFA] p-4">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Yıl Başından (YTD)</div>
          <div className="mt-2 flex items-baseline gap-3">
            <div className="num font-bold text-xl">{formatTL(c.ytd_total)}</div>
            <GrowthBadge pct={c.ytd_growth_pct} />
          </div>
          <div className="text-[10px] text-neutral-500 mt-1 num">
            {c.current_year} vs {c.previous_year} (Oca–{monthLabel(c.current_month).split(" ")[0]}): <span className="text-neutral-600">{formatTL(c.prev_ytd_total)}</span>
          </div>
        </div>
      </div>

      {(c.top_growers.length > 0 || c.top_decliners.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {c.top_growers.length > 0 && (
            <div className="border border-[#DCFCE7] rounded p-3">
              <div className="text-[10px] uppercase tracking-widest font-semibold text-[#16A34A] mb-2 flex items-center gap-1">
                <TrendingUp className="w-3 h-3" /> En Çok Büyüyen {cpLabel} (Bu Ay)
              </div>
              <div className="space-y-1">
                {c.top_growers.map(g => (
                  <div key={g.id || g.name} className="flex items-center justify-between text-xs px-2 py-1.5 hover:bg-[#F5F5F5]">
                    <span className="truncate font-medium">{g.name}</span>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="num text-[#16A34A] font-semibold">+{formatTL(g.diff)}</span>
                      <GrowthBadge pct={g.growth_pct} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {c.top_decliners.length > 0 && (
            <div className="border border-[#FEE2E2] rounded p-3">
              <div className="text-[10px] uppercase tracking-widest font-semibold text-[#DC2626] mb-2 flex items-center gap-1">
                <TrendingDown className="w-3 h-3" /> En Çok Düşen {cpLabel} (Bu Ay)
              </div>
              <div className="space-y-1">
                {c.top_decliners.map(g => (
                  <div key={g.id || g.name} className="flex items-center justify-between text-xs px-2 py-1.5 hover:bg-[#F5F5F5]">
                    <span className="truncate font-medium">{g.name}</span>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="num text-[#DC2626] font-semibold">{formatTL(g.diff)}</span>
                      <GrowthBadge pct={g.growth_pct} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
